import React, { useState, useEffect } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
  Field,
  Radio,
  RadioGroup,
} from "@headlessui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ComponentI } from "../../../../types";
import Card from "../../../../componentLibrary/Card";
import Input from "../../../../componentLibrary/Input";
import ExternalTextEditor from "../../../../componentLibrary/ExternalTextEditor";
import Checkbox from "../../../../componentLibrary/Checkbox";
import AddImageModal from "../../../../components/AddImageModal";
import Button from "../../../../componentLibrary/Button";
import { ImageI } from "../../../../types";
import { useGetImages } from "../../../../api/queries";
import { ImageTypeEnum } from "../../../../types";
import Spinner from "../../../../componentLibrary/Spinner";
import Tooltip from "../../../../componentLibrary/Tooltip";

export default function CalloutBoxContentCard({
  component,
  handleContentChange,
  provided,
}: {
  component: ComponentI;
  handleContentChange: (
    id: string,
    field: string,
    value: string | number | boolean,
  ) => void;
  provided?: any; //eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  const [imageType, setImageType] = useState(
    component.content.image_type || ImageTypeEnum.ICON,
  );
  const [query, setQuery] = useState("");
  const [filteredImages, setFilteredImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: images, isError, isLoading } = useGetImages(imageType);

  useEffect(() => {
    if (images) {
      const filteredImages1 =
        query === ""
          ? images
          : images.filter((image) =>
              image.name.toLowerCase().includes(query.toLowerCase()),
            );
      setFilteredImages(filteredImages1);
    }
  }, [images, query]);

  const useUploadedImage = (uploadedImage: ImageI) => {
    handleContentChange(component._id, "image_alt", uploadedImage.alt);
    handleContentChange(component._id, "image_src", uploadedImage.src);
    handleContentChange(component._id, "image_type", uploadedImage.type);
    setImageType(uploadedImage.type);
  };

  const setSelectedOption = (option) => {
    if (option) {
      const selectedImage = images.find((image) => image.src === option);
      if (selectedImage) {
        handleContentChange(component._id, "image_src", selectedImage.src);
        handleContentChange(component._id, "image_alt", selectedImage.alt);
        handleContentChange(component._id, "image_type", selectedImage.type);
      }
    }
  };

  const changeImageType = (option) => {
    setImageType(option);
    handleContentChange(component._id, "image_type", option);
    // Clear the current image when changing type
    handleContentChange(component._id, "image_src", "");
    handleContentChange(component._id, "image_alt", "");
  };

  const getImageFromURL = (url: string) => {
    const selectedImage = images?.find((image) => image.src === url);
    if (selectedImage) {
      return selectedImage;
    }
    return null;
  };

  if (isLoading) return <Spinner className="mx-auto" />;
  if (isError || !images) return <div>Error</div>;

  return (
    <Card>
      <div className="space-y-4">
        <Input
          value={component.content.word || ""}
          onChange={(e) =>
            handleContentChange(component._id, "word", e.target.value)
          }
          placeholder="Enter a title for the callout (optional)..."
          label="Callout Title (Optional)"
        />
        <ExternalTextEditor
          value={component.content.definition || ""}
          handleChange={(value) =>
            handleContentChange(component._id, "definition", value)
          }
          placeholder="Enter the callout content here..."
          label="Callout Content"
        />

        {/* Image Section */}
        <div className="space-y-2">
          <div className="flex flex-row justify-between items-center">
            <div
              className="w-2/3 relative"
              data-rbd-drag-handle-context-id={
                provided?.dragHandleProps?.["data-rbd-drag-handle-context-id"]
              }
              data-rbd-drag-handle-draggable-id="gibberish"
            >
              <Combobox
                immediate
                value={getImageFromURL(component.content?.image_src)}
                onChange={setSelectedOption}
              >
                <Label className="block text-sm font-medium text-gray-700">
                  Image (Optional)
                </Label>
                <ComboboxInput
                  aria-label="Image"
                  displayValue={(image: ImageI | null) => image?.name || ""}
                  onChange={(event) => setQuery(event.target.value)}
                  autoComplete="off"
                  placeholder="Pick from Existing Images"
                  className="w-full px-4 py-2 border border-[#0F172A] rounded-md focus:outline-none focus:border-[#00A3E0] disabled:cursor-not-allowed"
                  disabled={false}
                />
                <ComboboxOptions className="absolute mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm z-40">
                  {filteredImages.length === 0 && query !== "" ? (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      No results found
                    </div>
                  ) : (
                    filteredImages.map((option) => (
                      <ComboboxOption
                        key={option._id}
                        value={option.src}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? "bg-blue-100" : ""}`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center">
                            <img
                              src={option.src}
                              alt={option.alt}
                              className="h-6 w-6 flex-shrink-0"
                            />
                            <span
                              className={`ml-3 block truncate ${selected ? "font-medium" : "font-normal"}`}
                            >
                              {option.name}
                            </span>
                          </div>
                        )}
                      </ComboboxOption>
                    ))
                  )}
                </ComboboxOptions>
              </Combobox>
            </div>
            {component.content.image_src ? (
              <img
                src={component.content.image_src}
                alt={component.content.image_alt}
                className={`h-16 w-16 bg-gray-100 border-2 border-dashed ${component.content.image_hasBorder ? "border-[#00A3E0] rounded-md" : ""}`}
              />
            ) : (
              <div className="h-16 w-16 bg-gray-100 border-2 border-dashed"></div>
            )}
          </div>

          <div className="flex flex-row justify-between content-center">
            <RadioGroup
              value={imageType}
              onChange={changeImageType}
              aria-label="Image type"
              className={"flex flex-row justify-between gap-2"}
            >
              {Object.keys(ImageTypeEnum).map((imageType, index) => (
                <Field key={index} className="flex items-center gap-2">
                  <Radio
                    value={ImageTypeEnum[imageType]}
                    className="group flex size-6 items-center justify-center rounded-full bg-electrum-100 data-[checked]:bg-electrum-400"
                  ></Radio>
                  <Label>{ImageTypeEnum[imageType]}</Label>
                </Field>
              ))}
            </RadioGroup>
            <Button onClick={() => setIsModalOpen(true)}>
              Upload {imageType}
            </Button>
          </div>

          <div
            data-rbd-drag-handle-context-id={
              provided?.dragHandleProps?.["data-rbd-drag-handle-context-id"]
            }
            data-rbd-drag-handle-draggable-id="gibberish"
          >
            <div className="flex flex-row space-x-1 items-center">
              <div className="font-bold">Width</div>
              <Tooltip
                icon={
                  <InformationCircleIcon className="h-4 w-4 text-gray-600" />
                }
                text="Adjust the width of the image as a percentage of its container while maintaining its aspect ratio."
                position="right"
                isActive={true}
              />
            </div>
            <RadioGroup
              value={component.content.image_width || 100}
              onChange={(value) =>
                handleContentChange(component._id, "image_width", value)
              }
              className="flex flex-row justify-between gap-2 mt-2"
            >
              {[20, 40, 60, 80, 100].map((width) => (
                <Field key={width} className="flex items-center gap-2">
                  <Radio
                    value={width}
                    className="group flex size-6 items-center justify-center rounded-full bg-electrum-100 data-[checked]:bg-electrum-400"
                  ></Radio>
                  <Label>{width}</Label>
                </Field>
              ))}
            </RadioGroup>
          </div>

          <div className="flex items-center space-x-2 mt-2">
            <Checkbox
              label="Add border with rounded edges"
              checked={component.content.image_hasBorder || false}
              onChange={(e) => {
                const value: string | number | boolean = e.target.checked;
                handleContentChange(component._id, "image_hasBorder", value);
              }}
            />
            <Tooltip
              icon={<InformationCircleIcon className="h-4 w-4 text-gray-600" />}
              text="Apply a border with rounded corners to the image"
              position="right"
              isActive={true}
            />
          </div>

          <Input
            value={component.content.image_alt || ""}
            onChange={(e) =>
              handleContentChange(component._id, "image_alt", e.target.value)
            }
            label="Image Alt Text"
            placeholder="Callout image description"
          />
          <Input
            value={component.content.image_src || ""}
            onChange={(e) =>
              handleContentChange(component._id, "image_src", e.target.value)
            }
            label="Image Link"
            placeholder="www..."
          />
        </div>
      </div>
      <AddImageModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        useUploadedImage={useUploadedImage}
        type={imageType}
        showPrefill={true}
      />
    </Card>
  );
}
